/* eslint-disable array-bracket-newline  */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IWallet } from '@kattana-io/common';
import { IMobileFilterProps } from '../../../../components/SearchTable/SearchTable';
import {
  IWatchedWallet,
  WalletWatchlistTab,
  WatchlistWallet,
} from '../../../../reducers/wallet_watchlist';
import { IStore } from '../../../../reducers';
import { useNavigate, useParams } from 'react-router-dom';
import {
  actionAddWatchlistWallet,
  actionRemoveWatchlistWallet,
  actionSetWalletWatchlistChain,
  actionSetWalletWatchlistTab,
} from '../../../../actions/wallet_watchlist';
import { GetWalletFromWatchlistData } from '../../../../selectors/watchlist';
import {
  IMobileTableScema,
  formatMobileTableData,
} from '../../../../helpers/formatters';
import TableCardMobile from '../../../../molecules/Table/mobile/TableCard';
import WalletNicknameBalanceMobile from './WalletNicknameBalance';
import WalletAddressChainMobile from './WalletAddressChain';
import Loader from '../../../../components/common/Loader';

const WALLET_WATCHLIST_TABS = [
  WalletWatchlistTab.Star,
  WalletWatchlistTab.TopWallets,
  WalletWatchlistTab.BigTradesWallets,
  WalletWatchlistTab.ActiveWallets,
];

// as we don't have wallet_id for big trades wallets tab
// the only way to compare items is to use 'address-chain' unique key
const getKey = (address?: string, chain?: string) => `${address}-${chain}`;

// finds the same wallet in watchlist, otherwise returns undefined
const getWallet = (
  items: IWatchedWallet[],
  key: string,
  item: WatchlistWallet,
  tab: WalletWatchlistTab
): IWatchedWallet | undefined => {
  if (tab === WalletWatchlistTab.Star) {
    return item as IWatchedWallet;
  }
  const watchedItem = items.find(
    (wallet) => getKey(wallet.address, wallet.chain) === key
  );
  return watchedItem;
};

const walletWatchlistOptions = WALLET_WATCHLIST_TABS.map((tab) => ({
  label: tab,
  value: tab,
}));

interface IWalletWatchlistTableMobileProps {
  inputSearch: string;
  onMount: (data: IMobileFilterProps) => void;
  onBack: () => void;
}

const WalletWatchlistTableMobile = ({
  inputSearch,
  onMount,
  onBack,
}: IWalletWatchlistTableMobileProps) => {
  const watchedWallets = useSelector(
    (state: IStore) => state.wallet_watchlist.watchedWallets
  );
  const watchlistItems = useSelector(
    (state: IStore) => state.wallet_watchlist.wallets
  );
  const tab = useSelector((state: IStore) => state.wallet_watchlist.tab);
  const loading = useSelector(
    (state: IStore) => state.wallet_watchlist.loading
  );
  const walletTags = useSelector((state: IStore) => state.walletTags);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const chain = params.chain;
  const address = params.address;

  const [sortedData, setSortedData] = useState<{
    wallets?: WatchlistWallet[];
    selected?: string;
  }>();

  const { collection, searchedSortedData } = useMemo(() => {
    const collection = watchlistItems.filter((item) => {
      const tag = walletTags[`${item.chain}`]?.[`${item.address}`]?.[0];
      const searchStr = inputSearch.toLowerCase();
      return (
        item.address.toLowerCase().includes(searchStr) ||
        item.nickname.toLowerCase().includes(searchStr) ||
        tag?.toLowerCase().includes(searchStr)
      );
    });

    const searchedSortedData = sortedData?.wallets?.filter((item) => {
      const tag = walletTags[`${item.chain}`]?.[`${item.address}`]?.[0];
      const searchStr = inputSearch.toLowerCase();
      return (
        item.address.toLowerCase().includes(searchStr) ||
        item.nickname.toLowerCase().includes(searchStr) ||
        tag?.toLowerCase().includes(searchStr)
      );
    });
    return {
      collection,
      searchedSortedData,
    };
  }, [tab, watchlistItems, sortedData?.wallets, inputSearch]);

  useEffect(() => {
    onMount({
      setSortValue: setSortedData,
      setFilterValue: (chain: string) =>
        navigate(`/en/wallet-overview/${chain}/${address}`),
      initialData: {
        wallets: watchlistItems,
        selected: sortedData?.selected,
      },
      tabSelectorData: {
        options: walletWatchlistOptions,
        onSelect: (tab: string) =>
          dispatch(actionSetWalletWatchlistTab(tab as WalletWatchlistTab)),
        initial: { label: tab, value: tab },
        width: 160,
        height: '100%',
      },
    });
  }, [sortedData?.selected, setSortedData, watchlistItems, tab, address]);

  useEffect(() => {
    setSortedData(undefined);
  }, [tab]);

  useEffect(() => {
    if (chain) {
      dispatch(actionSetWalletWatchlistChain(chain));
    }
  }, [chain, dispatch]);

  const handleCardClick = useCallback(
    ({ chain, address }: IWallet) => {
      navigate(`/en/wallet-overview/${chain}/${address}`);
      onBack();
    },
    [navigate]
  );

  const onRemove = useCallback(
    (id: number) => dispatch(actionRemoveWatchlistWallet(id)),
    [dispatch]
  );

  const onAdd = useCallback(
    (data: GetWalletFromWatchlistData) =>
      dispatch(actionAddWatchlistWallet(data)),
    [dispatch]
  );

  const walletsSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'nickname_balance',
        parser: (_, row: WatchlistWallet) => {
          const itemKey = getKey(row.address, row.chain);
          const watchedItem = getWallet(watchedWallets, itemKey, row, tab);
          return (
            <WalletNicknameBalanceMobile
              wallet={row}
              actionRemoveWatchlist={watchedItem ? onRemove : undefined}
              actionAddWatchlist={watchedItem ? undefined : onAdd}
              id={watchedItem?.wallet_id}
            />
          );
        },
      },
      item_2: {
        accessor: 'price',
        parser: (_, row: WatchlistWallet) => (
          <WalletAddressChainMobile wallet={row} />
        ),
        width: 'max-content',
      },
    },
  };

  const parsedData = formatMobileTableData({
    data: searchedSortedData?.length ? searchedSortedData : collection,
    schema: walletsSchema,
    getColorForItem: () => 'grey',
    handleCardClick,
    keyName: ['chain', 'address'],
    spaceBetween: true,
  });

  return (
    <>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !collection?.length ? (
        <div className='centrify'>No data found</div>
      ) : (
        parsedData.map((row, index) => (
          <TableCardMobile key={index} data={{ ...row, opened: true }} />
        ))
      )}
    </>
  );
};

export default WalletWatchlistTableMobile;
